const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/components/PDFViewer/usePdfPinchZoom.ts';
import { MutableRefObject, useCallback, useEffect, useRef } from 'react';
import {
    buildWrapperLocalAnchor,
    clampPDFScale,
    getAnchorPosOnPage,
    getCommittedScale,
    getPreviewScaleBounds,
    getTouchDistance,
    getTouchMidpoint,
    mapClientPointToWrapperLocal,
    PageRect,
    ZoomAnchor,
} from './pdfZoomUtils';

export type PDFPinchZoomRefs = {
    pageRefs: MutableRefObject<Map<number, HTMLDivElement>>;
    scrollContainerRef: MutableRefObject<HTMLDivElement | null>;
    zoomWrapperRef: MutableRefObject<HTMLDivElement | null>;
};

type Params = PDFPinchZoomRefs & {
    enabled: boolean;
    scale: number;
    setScale: (scale: number) => void;
};

type State = 'IDLE' | 'PINCHING' | 'COMMITTING' | 'WHEEL';

type ZoomGestureBase = {
    initialScale: number;
    wrapperOrigin: { left: number; top: number };
    focalLocalX: number;
    focalLocalY: number;
    naturalWidth: number;
    naturalHeight: number;
    sPreview: number;
    panTx: number;
    panTy: number;
    capturedPageRects: PageRect[];
};

type PinchGesture = ZoomGestureBase & {
    initialMidpoint: { clientX: number; clientY: number };
    initialDistance: number;
    lastMidpoint: { clientX: number; clientY: number };
};

type WheelGesture = ZoomGestureBase & {
    initialClient: { clientX: number; clientY: number };
    lastClient: { clientX: number; clientY: number };
};

type CommitContext = {
    anchor: ZoomAnchor;
    fallbackTimerId: ReturnType<typeof setTimeout> | null;
};

const SKIP_COMMIT_EPSILON = 0.02;
const STUCK_COMMIT_FALLBACK_MS = 10000;
const WHEEL_COMMIT_DEBOUNCE_MS = 250;

// Snapshots wrapper-local page rects by subtracting the wrapper's untransformed origin
// from each page's bounding rect. Must be called while no transform is applied to the
// wrapper, otherwise the inputs would already be transformed.
const capturePageRectsLocal = (
    pageRefs: Map<number, HTMLDivElement>,
    wrapperOrigin: { left: number; top: number }
): PageRect[] =>
    Array.from(pageRefs.entries()).map(([pageNumber, el]) => {
        const r = el.getBoundingClientRect();
        return {
            pageNumber,
            rect: {
                left: r.left - wrapperOrigin.left,
                top: r.top - wrapperOrigin.top,
                right: r.right - wrapperOrigin.left,
                bottom: r.bottom - wrapperOrigin.top,
                width: r.width,
                height: r.height,
            },
        };
    });

export const usePdfPinchZoom = ({ enabled, pageRefs, scale, scrollContainerRef, setScale, zoomWrapperRef }: Params) => {
    const stateRef = useRef<State>('IDLE');
    const gestureRef = useRef<PinchGesture | null>(null);
    const commitRef = useRef<CommitContext | null>(null);

    const scaleRef = useRef(scale);
    scaleRef.current = scale;

    const runHandoff = useCallback(() => {
        const commit = commitRef.current;
        if (!commit) return;

        if (commit.fallbackTimerId !== null) {
            clearTimeout(commit.fallbackTimerId);
            commit.fallbackTimerId = null;
        }

        const wrapper = zoomWrapperRef.current;
        const container = scrollContainerRef.current;
        const pageEl = pageRefs.current.get(commit.anchor.pageNumber);

        if (wrapper) {
            wrapper.style.transform = '';
            wrapper.style.minWidth = '';
            wrapper.style.minHeight = '';
        }

        if (container && pageEl) {
            const pageRect = pageEl.getBoundingClientRect();
            const anchorPos = getAnchorPosOnPage(commit.anchor, pageRect);

            const dx = anchorPos.x - commit.anchor.focalX;
            const dy = anchorPos.y - commit.anchor.focalY;
            container.scrollLeft += dx;
            container.scrollTop += dy;
        }

        commitRef.current = null;
        stateRef.current = 'IDLE';
    }, []);

    const clearPendingCommit = useCallback(() => {
        const commit = commitRef.current;
        if (commit?.fallbackTimerId != null) {
            clearTimeout(commit.fallbackTimerId);
        }
        commitRef.current = null;
    }, []);

    const onFocalPageRendered = useCallback(
        (renderedPageNumber: number) => {
            if (stateRef.current !== 'COMMITTING') return;
            const commit = commitRef.current;
            if (!commit || commit.anchor.pageNumber !== renderedPageNumber) return;
            runHandoff();
        },
        [runHandoff]
    );

    const requestZoom = useCallback(
        (rawTargetScale: number) => {
            const targetScale = clampPDFScale(rawTargetScale);
            const wrapper = zoomWrapperRef.current;
            const container = scrollContainerRef.current;

            // Pre-load / refs missing, or no scale change: fall back to a plain scale set.
            if (!wrapper || !container || pageRefs.current.size === 0 || targetScale === scaleRef.current) {
                setScale(targetScale);
                return;
            }

            // Never interrupt an in-flight pinch/wheel gesture or commit.
            if (stateRef.current === 'COMMITTING' || stateRef.current === 'PINCHING' || stateRef.current === 'WHEEL') {
                return;
            }

            const wrapperRect = wrapper.getBoundingClientRect();
            const wrapperOrigin = { left: wrapperRect.left, top: wrapperRect.top };
            const capturedPageRects = capturePageRectsLocal(pageRefs.current, wrapperOrigin);

            const containerRect = container.getBoundingClientRect();
            const focalClient = {
                clientX: containerRect.left + containerRect.width / 2,
                clientY: containerRect.top + containerRect.height / 2,
            };

            // No live transform for programmatic zoom: pan = 0, previewScale = 1.
            const local = mapClientPointToWrapperLocal(focalClient, wrapperOrigin, { tx: 0, ty: 0 }, 1);
            const anchor = buildWrapperLocalAnchor(capturedPageRects, local, focalClient);
            if (!anchor) {
                setScale(targetScale);
                return;
            }

            commitRef.current = {
                anchor,
                fallbackTimerId: null,
            };
            commitRef.current.fallbackTimerId = setTimeout(runHandoff, STUCK_COMMIT_FALLBACK_MS);
            stateRef.current = 'COMMITTING';

            setScale(targetScale);
        },
        [runHandoff]
    );

    useEffect(() => {
        if (!enabled) return;

        const el = scrollContainerRef.current;
        if (!el) return;
        // NOTE: zoomWrapperRef.current is NOT captured at effect time. react-pdf's
        // <Document> shows a loading Message and doesn't render children (the wrapper)
        // until the PDF parses, which happens after this effect first runs. Refs don't
        // re-trigger effects, so we lazy-read the wrapper inside each handler instead.
        //
        // `cachedWrapper` keeps the most recently observed wrapper element so that
        // the unmount cleanup can still clear inline styles even after React has
        // nulled the ref (refs are nulled during the mutation phase, BEFORE useEffect
        // cleanups run in the passive phase).
        let cachedWrapper: HTMLDivElement | null = zoomWrapperRef.current;
        const getWrapper = () => {
            const current = zoomWrapperRef.current;
            if (current) cachedWrapper = current;
            return current ?? cachedWrapper;
        };

        const clearWrapperStyles = () => {
            const wrapper = getWrapper();
            if (!wrapper) return;
            wrapper.style.transform = '';
            wrapper.style.minWidth = '';
            wrapper.style.minHeight = '';
        };

        const beginPinch = (e: TouchEvent) => {
            // Block iOS Safari's native pinch / visual-viewport zoom before it
            // commits to the gesture. preventDefault on touchstart is the only
            // reliable mechanism iOS honours here — `user-scalable=no` and
            // `maximum-scale=1.0` are bypassed by the accessibility pinch path.
            e.preventDefault();
            e.stopPropagation();

            const wrapper = getWrapper();
            if (!wrapper) return;
            if (pageRefs.current.size === 0) return;

            const wrapperRect = wrapper.getBoundingClientRect();
            const wrapperOrigin = { left: wrapperRect.left, top: wrapperRect.top };
            const initialMidpoint = getTouchMidpoint(e.touches);

            gestureRef.current = {
                initialMidpoint,
                initialDistance: getTouchDistance(Array.from(e.touches)),
                focalLocalX: initialMidpoint.clientX - wrapperOrigin.left,
                focalLocalY: initialMidpoint.clientY - wrapperOrigin.top,
                panTx: 0,
                panTy: 0,
                sPreview: 1,
                lastMidpoint: initialMidpoint,
                wrapperOrigin,
                naturalWidth: wrapperRect.width,
                naturalHeight: wrapperRect.height,
                capturedPageRects: capturePageRectsLocal(pageRefs.current, wrapperOrigin),
                initialScale: scaleRef.current,
            };
            stateRef.current = 'PINCHING';
        };

        const updatePinch = (e: TouchEvent) => {
            const gesture = gestureRef.current;
            const wrapper = getWrapper();
            if (!gesture || !wrapper) return;

            e.preventDefault();
            e.stopPropagation();

            const currentDistance = getTouchDistance(Array.from(e.touches));
            const currentMidpoint = getTouchMidpoint(e.touches);

            const rawRatio = gesture.initialDistance > 0 ? currentDistance / gesture.initialDistance : 1;
            const { minPreviewScale, maxPreviewScale } = getPreviewScaleBounds(gesture.initialScale);
            const sPreview = Math.min(Math.max(rawRatio, minPreviewScale), maxPreviewScale);

            // Focal-preserving pan: the (1 - sPreview) term keeps the wrapper-local point
            // under the initial midpoint pinned to the moving midpoint, so content scales
            // around the user's fingers instead of from the wrapper's top-left. The
            // (midpoint - initialMidpoint) term lets the user pan as they pinch.
            const midpointDeltaX = currentMidpoint.clientX - gesture.initialMidpoint.clientX;
            const midpointDeltaY = currentMidpoint.clientY - gesture.initialMidpoint.clientY;
            const panTx = midpointDeltaX + gesture.focalLocalX * (1 - sPreview);
            const panTy = midpointDeltaY + gesture.focalLocalY * (1 - sPreview);

            gesture.panTx = panTx;
            gesture.panTy = panTy;
            gesture.sPreview = sPreview;
            gesture.lastMidpoint = currentMidpoint;

            wrapper.style.transform = `translate(${panTx}px, ${panTy}px) scale(${sPreview})`;
            wrapper.style.minWidth = `${gesture.naturalWidth * sPreview}px`;
            wrapper.style.minHeight = `${gesture.naturalHeight * sPreview}px`;
        };

        const endPinch = (e: TouchEvent) => {
            const gesture = gestureRef.current;
            if (!gesture) {
                stateRef.current = 'IDLE';
                return;
            }

            e.preventDefault();
            e.stopPropagation();

            // Skip-commit shortcut: pinch ratio is within ε of 1 (two-finger pan only).
            // Apply pan as scroll, no react-pdf re-render.
            if (Math.abs(gesture.sPreview - 1) < SKIP_COMMIT_EPSILON) {
                el.scrollLeft -= gesture.panTx;
                el.scrollTop -= gesture.panTy;
                clearWrapperStyles();
                gestureRef.current = null;
                stateRef.current = 'IDLE';
                return;
            }

            const newScale = getCommittedScale(gesture.initialScale, gesture.sPreview);
            if (newScale === gesture.initialScale) {
                // Rounding/clamping collapsed back to the starting scale — treat as skip-commit.
                el.scrollLeft -= gesture.panTx;
                el.scrollTop -= gesture.panTy;
                clearWrapperStyles();
                gestureRef.current = null;
                stateRef.current = 'IDLE';
                return;
            }

            const local = mapClientPointToWrapperLocal(
                gesture.lastMidpoint,
                gesture.wrapperOrigin,
                { tx: gesture.panTx, ty: gesture.panTy },
                gesture.sPreview
            );
            const anchor = buildWrapperLocalAnchor(gesture.capturedPageRects, local, gesture.lastMidpoint);
            if (!anchor) {
                el.scrollLeft -= gesture.panTx;
                el.scrollTop -= gesture.panTy;
                clearWrapperStyles();
                gestureRef.current = null;
                stateRef.current = 'IDLE';
                return;
            }

            commitRef.current = {
                anchor,
                fallbackTimerId: null,
            };
            commitRef.current.fallbackTimerId = setTimeout(runHandoff, STUCK_COMMIT_FALLBACK_MS);

            gestureRef.current = null;
            stateRef.current = 'COMMITTING';

            setScale(newScale);
        };

        const handleTouchStart = (e: TouchEvent) => {
            const state = stateRef.current;

            if (state === 'COMMITTING') return;

            if (state === 'PINCHING' && e.touches.length >= 3) {
                // Third finger — close out the gesture as if the user lifted up.
                endPinch(e);
                return;
            }

            if (state === 'IDLE' && e.touches.length === 2) {
                beginPinch(e);
            }
        };

        const handleTouchMove = (e: TouchEvent) => {
            if (stateRef.current !== 'PINCHING') return;
            if (e.touches.length !== 2) return;
            updatePinch(e);
        };

        const handleTouchEnd = (e: TouchEvent) => {
            if (stateRef.current !== 'PINCHING') return;
            endPinch(e);
        };

        const handleTouchCancel = () => {
            if (stateRef.current === 'PINCHING') {
                clearWrapperStyles();
                gestureRef.current = null;
                stateRef.current = 'IDLE';
            }
        };

        // iOS Safari fires its own gesture* events when two fingers are on screen. Their
        // default behaviour is page-level zoom intent, which would fight with ours.
        const blockGestureEvent = (e: Event) => {
            e.preventDefault();
            e.stopPropagation();
        };

        // touchstart is passive: false so the preventDefault inside beginPinch can
        // actually suppress iOS Safari's native pinch zoom.
        el.addEventListener('touchstart', handleTouchStart, { passive: false, capture: true });
        el.addEventListener('touchmove', handleTouchMove, { passive: false, capture: true });
        el.addEventListener('touchend', handleTouchEnd, { passive: false, capture: true });
        el.addEventListener('touchcancel', handleTouchCancel, { capture: true });
        el.addEventListener('gesturestart', blockGestureEvent as EventListener, { passive: false, capture: true });
        el.addEventListener('gesturechange', blockGestureEvent as EventListener, { passive: false, capture: true });
        el.addEventListener('gestureend', blockGestureEvent as EventListener, { passive: false, capture: true });

        return () => {
            clearPendingCommit();
            gestureRef.current = null;
            stateRef.current = 'IDLE';

            clearWrapperStyles();

            el.removeEventListener('touchstart', handleTouchStart, { capture: true });
            el.removeEventListener('touchmove', handleTouchMove, { capture: true });
            el.removeEventListener('touchend', handleTouchEnd, { capture: true });
            el.removeEventListener('touchcancel', handleTouchCancel, { capture: true });
            el.removeEventListener('gesturestart', blockGestureEvent as EventListener, { capture: true });
            el.removeEventListener('gesturechange', blockGestureEvent as EventListener, { capture: true });
            el.removeEventListener('gestureend', blockGestureEvent as EventListener, { capture: true });
        };
    }, [enabled, runHandoff]);

    // Wheel (ctrl/cmd) zoom is a desktop-only entry point
    useEffect(() => {
        if (!enabled) return;

        const el = scrollContainerRef.current;
        if (!el) return;

        let cachedWrapper: HTMLDivElement | null = zoomWrapperRef.current;
        const getWrapper = () => {
            const current = zoomWrapperRef.current;
            if (current) cachedWrapper = current;
            return current ?? cachedWrapper;
        };
        const clearWrapperStyles = () => {
            const wrapper = getWrapper();
            if (!wrapper) return;
            wrapper.style.transform = '';
            wrapper.style.minWidth = '';
            wrapper.style.minHeight = '';
        };

        let gesture: WheelGesture | null = null;
        let debounceId: ReturnType<typeof setTimeout> | null = null;

        const commitWheel = () => {
            debounceId = null;
            const g = gesture;
            gesture = null;
            if (!g) return;

            // Net zoom negligible (e.g. equal in/out scrolling) — drop the preview, no re-render.
            if (Math.abs(g.sPreview - 1) < SKIP_COMMIT_EPSILON) {
                clearWrapperStyles();
                stateRef.current = 'IDLE';
                return;
            }

            const newScale = getCommittedScale(g.initialScale, g.sPreview);
            if (newScale === g.initialScale) {
                // Rounding/clamping collapsed back to the starting scale — treat as skip-commit.
                clearWrapperStyles();
                stateRef.current = 'IDLE';
                return;
            }

            const local = mapClientPointToWrapperLocal(
                g.lastClient,
                g.wrapperOrigin,
                { tx: g.panTx, ty: g.panTy },
                g.sPreview
            );
            const anchor = buildWrapperLocalAnchor(g.capturedPageRects, local, g.lastClient);
            if (!anchor) {
                clearWrapperStyles();
                stateRef.current = 'IDLE';
                return;
            }

            commitRef.current = {
                anchor,
                fallbackTimerId: null,
            };
            commitRef.current.fallbackTimerId = setTimeout(runHandoff, STUCK_COMMIT_FALLBACK_MS);
            stateRef.current = 'COMMITTING';

            setScale(newScale);
        };

        const handleWheel = (e: WheelEvent) => {
            if (!e.ctrlKey && !e.metaKey) return;
            e.preventDefault();

            // Don't interfere with an in-flight commit or an active touch pinch.
            if (stateRef.current === 'COMMITTING' || stateRef.current === 'PINCHING') {
                return;
            }

            const wrapper = getWrapper();
            if (!wrapper) return;
            if (pageRefs.current.size === 0) return;

            // Start a fresh gesture when idle; otherwise accumulate onto the live preview.
            if (stateRef.current !== 'WHEEL' || !gesture) {
                const wrapperRect = wrapper.getBoundingClientRect();
                const wrapperOrigin = { left: wrapperRect.left, top: wrapperRect.top };
                gesture = {
                    wrapperOrigin,
                    capturedPageRects: capturePageRectsLocal(pageRefs.current, wrapperOrigin),
                    initialScale: scaleRef.current,
                    initialClient: { clientX: e.clientX, clientY: e.clientY },
                    focalLocalX: e.clientX - wrapperOrigin.left,
                    focalLocalY: e.clientY - wrapperOrigin.top,
                    naturalWidth: wrapperRect.width,
                    naturalHeight: wrapperRect.height,
                    sPreview: 1,
                    panTx: 0,
                    panTy: 0,
                    lastClient: { clientX: e.clientX, clientY: e.clientY },
                };
                stateRef.current = 'WHEEL';
            }

            const g = gesture;
            const factor = Math.exp(-e.deltaY / 400);
            const { minPreviewScale, maxPreviewScale } = getPreviewScaleBounds(g.initialScale);
            const sPreview = Math.min(Math.max(g.sPreview * factor, minPreviewScale), maxPreviewScale);

            // Focal-preserving pan around the cursor, mirroring updatePinch: the
            // focalLocal * (1 - sPreview) term pins the content point under the original
            // cursor, and the (client - initialClient) term tracks cursor movement mid-zoom.
            const panTx = e.clientX - g.initialClient.clientX + g.focalLocalX * (1 - sPreview);
            const panTy = e.clientY - g.initialClient.clientY + g.focalLocalY * (1 - sPreview);

            g.sPreview = sPreview;
            g.panTx = panTx;
            g.panTy = panTy;
            g.lastClient = { clientX: e.clientX, clientY: e.clientY };

            wrapper.style.transform = `translate(${panTx}px, ${panTy}px) scale(${sPreview})`;
            wrapper.style.minWidth = `${g.naturalWidth * sPreview}px`;
            wrapper.style.minHeight = `${g.naturalHeight * sPreview}px`;

            if (debounceId !== null) clearTimeout(debounceId);
            debounceId = setTimeout(commitWheel, WHEEL_COMMIT_DEBOUNCE_MS);
        };

        el.addEventListener('wheel', handleWheel, { passive: false });
        return () => {
            el.removeEventListener('wheel', handleWheel);
            if (debounceId !== null) clearTimeout(debounceId);
            clearPendingCommit();
            gesture = null;
            // Only reset if we were mid-preview; never stomp an in-flight commit/pinch.
            if (stateRef.current === 'WHEEL') {
                clearWrapperStyles();
                stateRef.current = 'IDLE';
            }
        };
    }, [enabled, runHandoff]);

    return { onFocalPageRendered, requestZoom };
};
